/**
 * Blog 리포지토리 — 서버 사이드 fetch (게스트 접근).
 * @description 주소는 숫자가 아니라 슬러그다. 서버도 그 이름으로 답한다.
 *
 *   서버 경로에는 `/web` 접두사가 붙는다. 계약의 경로는 `/blog` 이고, 웹 서버가 그 아래에
 *   마운트한 것이다.
 *
 *   언어는 `lang` 쿼리로 묻는다. 원문을 읽을 때는 싣지 않는다 — 원문 주소의 요청이 지금과
 *   같아야 캐시가 그대로 이어진다. 물어본 언어의 번역이 없으면 서버가 원문으로 답한다.
 */
import type { BlogDetail, BlogItem, BlogListResponse } from "../dtos";
import { mockBlogDetail, mockBlogItems } from "@/mocks/fixtures/blog";
import { ORIGINAL_BLOG_LANGUAGE, type BlogLanguage } from "@/shared/config";
import { fetchJson } from "@/shared/lib/http";

const BASE_URL = process.env.API_BASE_URL;
const USE_MOCK = process.env.NEXT_PUBLIC_API_MOCK === "1";

const withLanguage = (params: URLSearchParams, language: BlogLanguage) => {
  if (language !== ORIGINAL_BLOG_LANGUAGE) params.set("lang", language);
  return params;
};

/** GET /web/blog/:slug */
export function getBlogDetail(
  slug: string,
  language: BlogLanguage = ORIGINAL_BLOG_LANGUAGE,
): Promise<BlogDetail> {
  if (USE_MOCK) return mockBlogDetail(slug, language);
  const query = withLanguage(new URLSearchParams(), language).toString();
  return fetchJson(
    `${BASE_URL}/web/blog/${encodeURIComponent(slug)}${query ? `?${query}` : ""}`,
    `getBlogDetail(${slug}, ${language})`,
  );
}

/** 함께 보일 글의 수 — 매거진 바닥의 세 장 */
const RELATED_LIMIT = 3;

/**
 * GET /web/blog — 블로그에 실린 글을 발행 최신순으로.
 *
 * 운영자가 쓴 아티클과 운영자가 골라 올린 앱 글이 한 목록으로 섞여 온다. 웹에 보여도 되는 것만
 * 들어오므로 여기서 더 거를 것이 없다. 번역을 물어도 번역 없는 글이 빠지지 않고 원문으로
 * 실린다 — 글마다 [BlogItem.language] 를 봐야 한다.
 */
export function getBlogItems(
  limit: number,
  cursor?: string,
  language: BlogLanguage = ORIGINAL_BLOG_LANGUAGE,
): Promise<BlogListResponse> {
  if (USE_MOCK) return mockBlogItems(limit, cursor, language);
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  withLanguage(params, language);
  return fetchJson(`${BASE_URL}/web/blog?${params}`, "getBlogItems");
}

/**
 * 함께 볼 글 — 최신 글에서 지금 글을 뺀 앞 세 장.
 * @description 서버에 "이 글과 비슷한 글" 을 주는 자리가 없다. 목록을 한 장 더 받아 지금 글을
 *   빼는 것으로 대신한다. 주제가 같은 글을 고르려면 서버가 그 판단을 해 줘야 한다.
 */
export async function getRelatedPosts(
  slug: string,
  language: BlogLanguage = ORIGINAL_BLOG_LANGUAGE,
  limit = RELATED_LIMIT,
): Promise<BlogItem[]> {
  const { data } = await getBlogItems(limit + 1, undefined, language);
  return data.filter((it) => it.slug !== slug).slice(0, limit);
}
